import React from "react";
import { instance, readAddresses, useCatalogue } from "@clearabee/ui-sdk";
import { Button } from "components/Core/Button/Button";
import { Input } from "components/Core/Input/Input";
import { useTranslation } from "react-i18next";
import {
  displayErrorMessage,
  FlexGrid,
  Message,
  theme,
} from "@clearabee/ui-library-native";
import { useForm, Form } from "lib/hooks/useForm";
import { Spinner } from "components/Core/Spinner/Spinner";
import { formatPostcode } from "lib/utils";
import { Platform, View } from "react-native";
import { useQuestionContext } from "lib/providers/questionProvider";
import { useState } from "react";
import { FormState } from "./Skips.validation";
import { SKIP_WASTE_TYPES } from "lib/constants/skipWasteTypes";
import { z } from "zod";
import { postcode } from "lib/schemas";

export const WasteTypeAndPostcode = (): React.ReactElement => {
  const [translate] = useTranslation();
  const context = useQuestionContext<FormState>();
  const { setCatalogue } = useCatalogue();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const wasteTypeAndPostcode = context.get("wasteTypeAndPostcode");

  const form = useForm({
    defaultValues: {
      wasteType: wasteTypeAndPostcode?.wasteType ?? "",
      postcode: wasteTypeAndPostcode?.postcode ?? "",
    },
    validationSchema: z.object({
      postcode: postcode,
      wasteType: z.string().min(1),
    }),
    onSubmit: async ({ postcode, wasteType }) => {
      try {
        setLoading(true);
        setError(undefined);

        const { data } = await instance.catalogues.getCatalogue("hwa", {
          params: { postcode, flatten: true },
        });

        const { addresses } = await readAddresses(postcode);

        if (!addresses.length) throw new Error();

        context
          .set("wasteTypeAndPostcode", { postcode, wasteType })
          .open("product");
        setCatalogue(postcode, data);
      } catch {
        return setError(
          `${translate(
            "hwa.components.postcodeResultMessage.unavailablePostcode",
          )} ${formatPostcode(postcode)}`,
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Form state={form} useForm={false}>
        {({ Field }) => (
          <View style={{ maxWidth: 600, width: "100%", alignSelf: "center" }}>
            <FlexGrid rowSpacing={null}>
              <FlexGrid.Cell base="100%" medium="50%">
                <Field
                  name="wasteType"
                  errorStyles={Platform.select({
                    web: {
                      position: "absolute",
                      bottom: -29,
                    },
                    default: {
                      marginBottom: -theme.spacing.xsmall,
                    },
                  })}
                  labelStyles={{
                    color:
                      Platform.OS === "web"
                        ? theme.colors.light.base
                        : theme.colors.dark.base,
                  }}
                  invertErrorColors
                  errorBackground
                  label={translate("common.wasteType")}
                >
                  {({ field }) => (
                    <Input.Select
                      {...field}
                      options={SKIP_WASTE_TYPES.map(({ label, value }) => ({
                        label: translate(label),
                        value,
                      }))}
                      placeholder={translate(
                        "common.form.placeholder.wasteType",
                      )}
                      disabled={isLoading}
                      usePortal
                    />
                  )}
                </Field>
              </FlexGrid.Cell>
              <FlexGrid.Cell base="100%" medium="50%">
                <Field
                  name="postcode"
                  errorStyles={Platform.select({
                    web: {
                      position: "absolute",
                      bottom: -29,
                    },
                    default: {
                      marginBottom: -theme.spacing.xsmall,
                    },
                  })}
                  labelStyles={{
                    color:
                      Platform.OS === "web"
                        ? theme.colors.light.base
                        : theme.colors.dark.base,
                  }}
                  invertErrorColors
                  errorBackground
                  label={translate("common.postcode")}
                >
                  {({ field }) => (
                    <Input.Text
                      {...field}
                      disabled={isLoading}
                      placeholder={translate(
                        "common.form.placeholder.postcode",
                      )}
                    />
                  )}
                </Field>
              </FlexGrid.Cell>
            </FlexGrid>

            {displayErrorMessage(error, (props) => (
              <View
                style={{ marginTop: theme.spacing.medium, alignSelf: "center" }}
              >
                <Message
                  {...props}
                  type="error"
                  styles={{
                    backgroundColor: theme.colors.negative.base,
                  }}
                  color="light"
                  background
                >
                  {props.children}
                </Message>
              </View>
            ))}

            <Button
              disabled={isLoading}
              type="button"
              color="warning"
              size="medium"
              styles={{
                marginTop: theme.spacing.medium,
                height: 54,
                alignSelf: "center",
              }}
              onClick={form.submitForm}
            >
              {isLoading ? <Spinner /> : translate("common.continue")}
            </Button>
          </View>
        )}
      </Form>
    </>
  );
};
